/**
 * Profile > Public pages: the garden page, in one box.
 *
 * Every gardener's garden page has a name, read as a handle:
 * grow.gardengameplan.com/@amy. The backend gives one the first time the
 * page is asked for (from the "Show me as" handle or the first word of the
 * display name, else two garden words and a number), so no one has to hand
 * out the random code (/g/Wb8yDjB3). That still works, and moves a visitor
 * on to the name, but is never shown here. The box:
 *
 * - "Your garden page", and the privacy line once: anyone with the address
 *   sees what the gardener made public, never their address or notes.
 * - The link, grow.gardengameplan.com/@amy, then Share · Copy link · Change,
 *   the same buttons and words as every sharer (ShareButtons' LinkShareRow;
 *   Share only where the browser has a share menu). Change opens the
 *   field below, filled with the name, with Save, Cancel, and the note that
 *   the old name stops working and no one else can take it for the server's
 *   hold days (MyGarden.rules.holdDays, the Grow setting address.HOLD_DAYS).
 * - Only if the backend has given no name (its database without the
 *   address column, or a failure): the code's link · Share · Copy link, so the box still
 *   gives a link that works.
 *
 * As the gardener types, the line under the field says whether the name is
 * free ("Available") or why not, asked of the backend once typing pauses
 * (GARDEN_ADDRESS_CHECK_DELAY_MS). The backend alone judges what is typed
 * (GET /address/check): it drops a leading "@" and accents ("Begoña" is
 * "begona"), lower-cases, and says too short, too long, the wrong characters,
 * a hyphen at an end, reserved, blocked, taken or held. The app keeps none of
 * those rules (audit A43); it shows the name the backend understood. The
 * field's limit and the lengths in the words come from MyGarden.rules.
 *
 * A check or a save that fails on our side (a 5xx or no answer) says the
 * standard sentence, reported to Sentry, with the support address as a link
 * (D31); a check that failed used to leave "Checking" spinning for ever.
 *
 * Used by: PublicPageCard (Profile > Public pages). The host shown before
 * "@" is shareBaseUrl (VITE_SHARE_BASE_URL, else this app's address).
 *
 * @module GardenPageRow
 */

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { useTranslation } from '@/shared/hooks/useTranslation';
import {
  addressRefusal,
  checkGardenAddress,
  gardenUrlFor,
  setGardenAddress,
  type GardenAddressProblem,
  type MyGarden,
} from '@/shared/api/gardens';
import { shareBaseUrl } from '@/shared/api/shares';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { isServerFailure, serverFailureText } from '@/shared/lib/serverFailure';
import { cn } from '@/lib/utils';
import {
  GARDEN_ADDRESS_CHECK_DELAY_MS,
  MY_GARDEN_QUERY_KEY,
} from '@/config/tunables/garden';
import { LinkShareRow } from '@/features/share/ShareButtons';
import { SHARE_BUTTON_CLASS } from '@/features/share/lib/shareLink';
import { SettingRow } from './profileKit';

/** The React Query key a name's availability is cached under, with the name. */
const ADDRESS_CHECK_QUERY_KEY = 'garden-address-check';

/**
 * A value that follows another once it has stopped changing for a while.
 *
 * @param value - The changing value.
 * @param delayMs - How long it must hold still.
 * @returns The value as it was when it last held still.
 */
function useSettled<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/**
 * The field for changing the page's name: "New name:", the app's address
 * and "@" before the box, Save and Cancel, the line saying whether the name
 * is free, and the note about the old name.
 *
 * @param garden - The gardener's garden page (getMyGarden), with a name.
 * @param onDone - Called once a name is kept, or the change is cancelled.
 * @param onSaved - Called once a name is kept, to tick "Saved".
 */
const GardenNameField: React.FC<{
  garden: MyGarden;
  onDone: () => void;
  onSaved: () => void;
}> = ({ garden, onDone, onSaved }) => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const inputId = React.useId();
  const statusId = React.useId();
  /** What is in the box: their name, until they type. */
  const [draft, setDraft] = React.useState(garden.slug ?? '');
  const [typed, setTyped] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  /** A refusal from Save, or another failure's sentence. */
  const [refused, setRefused] = React.useState<GardenAddressProblem | null>(null);
  const [failed, setFailed] = React.useState<string | null>(null);

  // Until the gardener types, the box follows what the server says.
  React.useEffect(() => {
    if (!typed) setDraft(garden.slug ?? '');
  }, [garden.slug, typed]);

  /** What is typed, trimmed; the backend folds and judges it (GET /address/check). */
  const typedName = draft.trim();
  const settled = useSettled(typedName, GARDEN_ADDRESS_CHECK_DELAY_MS);
  const { data: check, isFetching, error: checkError } = useQuery({
    queryKey: [ADDRESS_CHECK_QUERY_KEY, settled],
    queryFn: () => checkGardenAddress(settled),
    enabled: Boolean(settled) && settled !== garden.slug,
    staleTime: 0,
  });
  /** The server's address rules: the field's limit and the lengths and hold the words name. */
  const rules = check?.rules ?? garden.rules;
  /** The backend's answer for what is typed now, once typing has paused on it. */
  const checked = check && settled === typedName ? check : null;
  const unchanged = typedName === (garden.slug ?? '') || Boolean(checked?.mine);
  // A check that failed on our side says so (the standard sentence, reported),
  // rather than "Checking" for ever.
  const checkFailed = !unchanged && !isFetching && settled === typedName && isServerFailure(checkError)
    ? serverFailureText(checkError, 'GardenPageRow')
    : null;
  const waiting = !unchanged && Boolean(typedName) && (!checked || isFetching || settled !== typedName) && !checkFailed;
  const problem: GardenAddressProblem | null = refused ?? (checked && !checked.available && !checked.mine ? checked.reason : null);
  const canSave = Boolean(typedName) && !unchanged && !waiting && Boolean(checked?.available) && !saving;

  /**
   * The words for a problem, in the reader's language.
   *
   * @param reason - Why the name cannot be had.
   */
  const problemWords = (reason: GardenAddressProblem): string => {
    switch (reason) {
      case 'short': return t('profile.public.addressShort', { min: rules.minLength });
      case 'long': return t('profile.public.addressLong', { max: rules.maxLength });
      case 'characters': return t('profile.public.addressCharacters');
      case 'hyphen': return t('profile.public.addressHyphen');
      case 'reserved': return t('profile.public.addressReserved');
      case 'blocked': return t('profile.public.addressBlocked');
      case 'held': return t('profile.public.addressHeld');
      default: return t('profile.public.addressTaken');
    }
  };

  /**
   * Keeps a name, and puts the answer in the garden page's cache so the
   * link above changes at once.
   *
   * @param next - The name.
   */
  const save = async (next: string): Promise<void> => {
    setSaving(true);
    setRefused(null);
    setFailed(null);
    try {
      const result = await setGardenAddress(next);
      queryClient.setQueryData<MyGarden | null>([MY_GARDEN_QUERY_KEY], (old) => (old ? { ...old, slug: result.slug, path: result.path } : old));
      setTyped(false);
      onSaved();
      onDone();
    } catch (err) {
      const reason = addressRefusal(err);
      if (reason) setRefused(reason);
      else setFailed(errorMessage(err, t('profile.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const host = shareBaseUrl().replace(/^https?:\/\//, '');
  let status: React.ReactNode = null;
  if (failed) status = <span className="text-destructive"><FailureText text={failed} /></span>;
  else if (problem) status = <span className="text-destructive">{problemWords(problem)}</span>;
  else if (checkFailed) status = <span className="text-destructive"><FailureText text={checkFailed} /></span>;
  else if (unchanged && garden.slug) status = <span className="flex items-center gap-1 text-muted-foreground"><Check className="h-3.5 w-3.5" aria-hidden="true" />{t('profile.public.addressMine')}</span>;
  else if (waiting) status = <span className="flex items-center gap-1 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />{t('profile.public.addressChecking')}</span>;
  else if (checked?.available) status = <span className="flex items-center gap-1 text-primary"><Check className="h-3.5 w-3.5" aria-hidden="true" />{t('profile.public.addressAvailable')}</span>;

  return (
    <div className="space-y-1 pt-1">
      <form
        className="flex flex-wrap items-center gap-x-2 gap-y-1.5"
        onSubmit={(event) => { event.preventDefault(); if (canSave && checked) void save(checked.slug); }}
      >
        {/* The app's address and "@" stand before the box, not in it, so on
            a phone they take their own line and the box keeps its width. */}
        <label htmlFor={inputId} className="text-sm">
          {t('profile.public.addressNew')}{' '}
          <span className="break-all text-muted-foreground">{host}/@</span>
        </label>
        <div className="flex min-w-[14rem] flex-1 items-center gap-2">
          <div className={cn(
            'flex h-9 min-w-0 flex-1 items-center rounded-md border border-input text-sm shadow-sm focus-within:ring-1 focus-within:ring-ring',
            problem && 'border-destructive',
          )}>
            <input
              id={inputId}
              value={draft}
              onChange={(event) => { setDraft(event.target.value); setTyped(true); setRefused(null); setFailed(null); }}
              maxLength={rules.maxLength}
              placeholder={t('profile.public.addressPlaceholder')}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              aria-describedby={statusId}
              aria-invalid={problem ? true : undefined}
              className="h-full min-w-0 flex-1 bg-transparent px-3 outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>
        {/* Cancel left, Save right, on their own line so the box keeps its width on a phone. */}
        <div className="w-full">
          <ActionRow onCancel={onDone} cancelLabel={t('profile.public.addressCancel')} disabled={saving}>
            <Button type="submit" loading={saving} disabled={!canSave}>
              {t('profile.public.addressSave')}
            </Button>
          </ActionRow>
        </div>
      </form>
      <p id={statusId} className="min-h-[1.25rem] text-xs" aria-live="polite">{status}</p>
      {rules && <p className="text-xs text-muted-foreground">{t('profile.public.addressChange', { days: rules.holdDays })}</p>}
    </div>
  );
};

/**
 * The garden page's box: its link with Share, Copy link and Change, and the field to
 * change its name, as the module comment lays out.
 *
 * @param garden - The gardener's garden page (getMyGarden).
 * @param saved - Whether "Saved" shows beside the title.
 * @param onSaved - Called once a name is kept, to tick "Saved".
 */
export const GardenPageRow: React.FC<{ garden: MyGarden; saved: boolean; onSaved: () => void }> = ({ garden, saved, onSaved }) => {
  const { reactTranslate: t } = useTranslation();
  /** Whether the field is open to change the name they have. */
  const [changing, setChanging] = React.useState(false);
  const named = Boolean(garden.slug);
  const url = gardenUrlFor(garden.code, garden.slug);

  return (
    <SettingRow title={t('profile.public.gardenLink')} hint={t('profile.public.gardenLinkHint')} saved={saved} stacked>
      {/* The link, then Share and Copy link as every sharer has them (ShareButtons), and Change. */}
      <LinkShareRow link={url} title={t('gardenPage.page.title')}>
        {named && !changing && (
          <Button variant="outline" className={SHARE_BUTTON_CLASS} onClick={() => setChanging(true)}>
            {t('profile.public.addressChangeOpen')}
          </Button>
        )}
      </LinkShareRow>
      {named && changing && (
        <GardenNameField garden={garden} onDone={() => setChanging(false)} onSaved={onSaved} />
      )}
    </SettingRow>
  );
};
